import { IComponentOptions, IController, IPromise } from "angular";
import { IModalScope, IModalService } from "angular-ui-bootstrap";

import { generateExistingAttachment } from "../helpers";
import { AttachmentService } from "../services/attachmentService";
import { IUiAttachment, INewAttachment, IAttachment } from "../models";
import { PreviewImageService } from "./preview-image/preview-image";
import { FileService, IFileUploadedData } from "@gtmhub/files";
import { UIErrorHandlingService } from "@gtmhub/error-handling";

export class AttachmentCtrl implements IController {
  attachment: IUiAttachment;
  previewBackgroundImage: string;
  static $inject = ["$uibModal", "AttachmentService", "FileService", "PreviewImageService", "UIErrorHandlingService"];

  constructor(
    private $uibModal: IModalService,
    private attachmentService: AttachmentService,
    private fileService: FileService,
    private previewImageService: PreviewImageService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {}

  $onInit(): void {
    if (this.attachment.uploading) {
      this.uploadFileAndCreateAttachment().then(() => this.generateImagePreviewUrl());
    } else {
      this.generateImagePreviewUrl();
    }
  }

  download(): void {
    this.fileService
      .downloadFile(this.attachment.existing.url, this.attachment.existing.fileName)
      .catch((rejection) => this.uiErrorHandlingService.handleModal(rejection, { hideBody: true }));
  }

  confirmDelete(): void {
    interface IConfirmDeleteAttachmentScope extends IModalScope {
      fileName: string;
      confirm(): void;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("./confirm-delete-attachment.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IConfirmDeleteAttachmentScope) {
          $scope.fileName = ctrl.attachment.existing.fileName;

          $scope.confirm = (): void => {
            ctrl.delete();
            $scope.$close();
          };
        },
      ],
    });
  }

  previewImage(): void {
    if (this.isAttachmentImage()) {
      this.previewImageService.open(this.attachment.existing.url);
    }
  }

  private delete = (): void => {
    this.attachment.existing.deleting = true;

    this.attachmentService.deleteAttachment(this.attachment.existing.id).then(
      () => {
        // deleting file response can be left unhandled because it won't trouble the user in any way
        // if there is an error it will be logged by error handling service and file will stay in our storage
        this.fileService.deleteFile(this.attachment.existing.url);

        // setting existing prop to null will hide the attachment from the list of attachments
        // as the other prop uploading should be null as well
        this.attachment.existing = null;
      },
      (rejection) => {
        this.attachment.existing.deleting = false;
        this.uiErrorHandlingService.handleModal(rejection);
      }
    );
  };

  private uploadFileAndCreateAttachment = (): IPromise<void | IAttachment> => {
    const { file, targetId, targetType } = this.attachment.uploading;

    return this.fileService.uploadFile({ file, targetId, targetType, ref: "attachment" }).then(
      (data) => this.createAttachment(data),
      (rejection) => {
        // hide attachment from collection
        this.attachment.uploading = null;
        this.uiErrorHandlingService.handleModal(rejection);
      },
      (progress: number) => {
        this.attachment.uploading.progress = progress;
      }
    );
  };

  private createAttachment = (data: IFileUploadedData): IPromise<void | IAttachment> => {
    const newAttachment: INewAttachment = {
      targetId: this.attachment.uploading.targetId,
      targetType: this.attachment.uploading.targetType,
      fileName: this.attachment.uploading.file.name,
      size: data.size,
      contentType: data.contentType,
      url: data.url,
      previewUrl: data.previewUrl,
    };

    return this.attachmentService.createAttachment(newAttachment).then(
      (createdAttachment) => {
        this.attachment.uploading = null;
        this.attachment.existing = generateExistingAttachment(createdAttachment);
      },
      (rejection) => {
        // delete file without handling response
        this.fileService.deleteFile(data.url);
        // hide attachment from collection
        this.attachment.uploading = null;

        this.uiErrorHandlingService.handleModal(rejection);
      }
    );
  };

  private generateImagePreviewUrl(): void {
    if (this.isAttachmentImage()) {
      this.fileService.generateFileUrl(this.attachment.existing.previewUrl).then(
        (url) => (this.previewBackgroundImage = `url(${url})`),
        (rejection) => this.uiErrorHandlingService.handleModal(rejection, { hideBody: true })
      );
    }
  }

  private isAttachmentImage = (): boolean => !!this.attachment.existing && !!this.attachment.existing.previewUrl;
}

export const AttachmentComponent: IComponentOptions = {
  templateUrl: require("./attachment.jade"),
  controller: AttachmentCtrl,
  bindings: {
    attachment: "<",
    disabled: "<",
  },
};
